/* PPE Parts & Supply: catalog filtering, sorting, view toggle and search suggestions.
   Custom elements:
   - <facet-filters data-section-id>  (sections/main-collection, sections/main-search, snippets/facets)
     Contract: form[data-facets-form], [data-facets-region="name"] (replaced from the Section Rendering API),
     [data-facets-drawer], [data-facets-open], [data-facets-close], [data-facets-sort], [data-price-input],
     [data-price-track], [data-facet-id] + [data-facet-more], [data-view], [data-facets-grid], a[data-facets-link].
   - <search-suggestions data-terms data-search-url> (sections/main-search)
     Contract: [data-suggest-queries] + [data-suggest-list], [data-suggest-collections],
     [data-suggest-try-card] + [data-suggest-try], [data-suggest-category-meta].
   Everything degrades to plain GET forms and links without JavaScript. */
(function () {
  'use strict';

  const getTheme = () => window.theme || {};
  const VIEW_KEY = 'ppe:catalog-view';
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const parse = (html) => {
    const theme = getTheme();
    return typeof theme.parseHTML === 'function' ? theme.parseHTML(html) : new DOMParser().parseFromString(html, 'text/html');
  };

  const announce = (message) => {
    const theme = getTheme();
    if (message && typeof theme.announce === 'function') theme.announce(message);
  };

  const onMediaChange = (query, handler) => {
    if (query.addEventListener) query.addEventListener('change', handler);
    else if (query.addListener) query.addListener(handler);
  };

  const offMediaChange = (query, handler) => {
    if (query.removeEventListener) query.removeEventListener('change', handler);
    else if (query.removeListener) query.removeListener(handler);
  };

  /* ------------------------------------------------------------------
     <facet-filters>
     ------------------------------------------------------------------ */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound) return;
      this.dataset.bound = 'true';

      this.sectionId = this.dataset.sectionId;
      this.root = this.closest('.shopify-section') || document;
      this.expanded = new Set();
      this.requestId = 0;
      this.currentSearch = window.location.search;

      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));

      this.onPopState = () => {
        if (window.location.search === this.currentSearch) return;
        this.currentSearch = window.location.search;
        this.syncSort();
        this.render(window.location.search.replace(/^\?/, ''), { push: false });
      };
      window.addEventListener('popstate', this.onPopState);

      this.onBreakpoint = (event) => {
        if (event.matches) this.closeDrawer(false);
      };
      onMediaChange(desktopQuery, this.onBreakpoint);

      this.applyView(this.readView());
      this.bindStickySidebar();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('scroll', this.onStickyScroll);
      window.removeEventListener('resize', this.onStickyScroll);
      if (this.sidebarObserver) this.sidebarObserver.disconnect();
      offMediaChange(desktopQuery, this.onBreakpoint);
      clearTimeout(this.priceTimer);
    }

    /* The sidebar is sticky with no scrollbar of its own. When it is taller
       than the window, its sticky offset follows the page scroll between two
       limits, so scrolling down stops at its last filter and scrolling up
       stops at its first. */
    bindStickySidebar() {
      const sidebar = this.querySelector('.catalog__sidebar');
      if (!sidebar) return;
      const GAP = 24;
      let top = GAP;
      let lastY = window.scrollY;

      this.onStickyScroll = () => {
        const y = window.scrollY;
        // On big screens the page is zoomed: window sizes and scroll come in screen pixels, the sidebar in page pixels.
        const zoom = parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
        const lowest = Math.min(GAP, window.innerHeight / zoom - sidebar.offsetHeight - GAP);
        top = desktopQuery.matches ? Math.max(lowest, Math.min(GAP, top - (y - lastY) / zoom)) : GAP;
        lastY = y;
        sidebar.style.setProperty('--catalog-sidebar-top', `${top}px`);
      };

      window.addEventListener('scroll', this.onStickyScroll, { passive: true });
      window.addEventListener('resize', this.onStickyScroll);
      if ('ResizeObserver' in window) {
        this.sidebarObserver = new ResizeObserver(this.onStickyScroll);
        this.sidebarObserver.observe(sidebar);
      }
      this.onStickyScroll();
    }

    get form() {
      return this.querySelector('[data-facets-form]');
    }

    /* ---------- Events ---------- */

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-price-input]')) return; // debounced via input, or submitted with Enter / Apply
      if (!target.form || !target.form.matches('[data-facets-form]')) return;
      this.submitForm();
    }

    onInput(event) {
      const target = event.target;
      if (target.matches('[data-price-range]')) {
        this.onRangeInput(target);
        return;
      }
      if (!target.matches('[data-price-input]')) return;
      const facet = target.closest('[data-facet-id]');
      this.updateTrack(facet);
      clearTimeout(this.priceTimer);
      /* Wait for a pause in typing, and never reload on a half-typed maximum
         that is still below the minimum. */
      const minInput = facet.querySelector('[data-price-input="min"]');
      const maxInput = facet.querySelector('[data-price-input="max"]');
      if (minInput && maxInput && minInput.value !== '' && maxInput.value !== '' && Number(maxInput.value) < Number(minInput.value)) return;
      this.priceTimer = setTimeout(() => this.submitForm(), 800);
    }

    onSubmit(event) {
      if (!event.target.matches('[data-facets-form]')) return;
      event.preventDefault();
      this.submitForm();
    }

    onClick(event) {
      const target = event.target;

      const open = target.closest('[data-facets-open]');
      if (open) {
        this.openDrawer(open);
        return;
      }

      if (target.closest('[data-facets-close]')) {
        this.closeDrawer(true);
        return;
      }

      const viewButton = target.closest('[data-view]');
      if (viewButton) {
        const view = viewButton.dataset.view === 'list' ? 'list' : 'grid';
        try {
          window.localStorage.setItem(VIEW_KEY, view);
        } catch (e) {
          /* storage unavailable */
        }
        this.applyView(view);
        return;
      }

      const more = target.closest('[data-facet-more]');
      if (more) {
        const facet = more.closest('[data-facet-id]');
        const expand = more.getAttribute('aria-expanded') !== 'true';
        this.setExpanded(facet, expand);
        if (expand) this.expanded.add(facet.dataset.facetId);
        else this.expanded.delete(facet.dataset.facetId);
        return;
      }

      const link = target.closest('a[data-facets-link], [data-facets-region="results"] .pagination a');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      event.preventDefault();
      url.searchParams.delete('section_id');
      this.render(url.searchParams.toString(), { focus: link.closest('.pagination') ? 'results' : 'summary' });
    }

    onKeydown(event) {
      const drawer = this.querySelector('[data-facets-drawer].is-open');
      if (!drawer) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer(true);
      } else if (event.key === 'Tab') {
        this.trapFocus(event, drawer);
      }
    }

    /* ---------- Rendering ---------- */

    formQuery() {
      const form = this.form;
      if (!form) return window.location.search.replace(/^\?/, '');
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (typeof value !== 'string') return;
        const trimmed = value.trim();
        if (trimmed !== '') params.append(key, trimmed);
      });
      return params.toString();
    }

    submitForm() {
      clearTimeout(this.priceTimer);
      this.render(this.formQuery());
    }

    async render(query, options = {}) {
      const push = options.push !== false;
      const id = ++this.requestId;
      const path = window.location.pathname;
      const pageUrl = query ? `${path}?${query}` : path;
      const fetchParams = new URLSearchParams(query);
      fetchParams.set('section_id', this.sectionId);

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setLoading(true);

      try {
        const response = await fetch(`${path}?${fetchParams.toString()}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        if (id !== this.requestId) return;

        const doc = parse(html);
        this.swap(doc, options.focus);

        if (push) window.history.pushState({ facets: this.sectionId }, '', pageUrl);
        this.currentSearch = window.location.search;

        const results = doc.querySelector('[data-facets-region="results"]');
        announce(results && results.getAttribute('data-facets-count'));
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(pageUrl);
      } finally {
        if (id === this.requestId) this.setLoading(false);
      }
    }

    swap(doc, focusTarget) {
      const active = document.activeElement;
      const activeId = active && active !== document.body && this.root.contains(active) ? active.id : '';

      this.root.querySelectorAll('[data-facets-region]').forEach((region) => {
        const name = region.dataset.facetsRegion;
        const fresh = doc.querySelector(`[data-facets-region="${name}"]`);
        if (!fresh) return;
        if (name === 'filters') {
          this.swapFilters(region, fresh);
        } else {
          region.innerHTML = fresh.innerHTML;
        }
        const count = fresh.getAttribute('data-facets-count');
        if (count !== null) region.setAttribute('data-facets-count', count);
      });

      this.querySelectorAll('[data-facet-id]').forEach((facet) => {
        if (this.expanded.has(facet.dataset.facetId)) this.setExpanded(facet, true);
      });
      this.applyView(this.readView());
      this.restoreFocus(activeId, focusTarget);
    }

    /* Replace every filter card except a price card the shopper is typing in */
    swapFilters(region, fresh) {
      const focused = document.activeElement;
      const keep = focused && focused.matches('[data-price-input]') && region.contains(focused)
        ? focused.closest('[data-facet-id]')
        : null;

      Array.from(region.children).forEach((child) => {
        if (child !== keep) child.remove();
      });

      const freshChildren = Array.from(fresh.children);
      const keepIndex = keep ? freshChildren.findIndex((child) => child.dataset.facetId === keep.dataset.facetId) : -1;

      freshChildren.forEach((child, index) => {
        if (index === keepIndex) return;
        const node = document.importNode(child, true);
        if (keep && (keepIndex === -1 || index < keepIndex)) region.insertBefore(node, keep);
        else region.appendChild(node);
      });

      if (keep && keepIndex === -1) keep.remove();
    }

    restoreFocus(activeId, focusTarget) {
      const focusIsLost = !document.activeElement || document.activeElement === document.body || !document.activeElement.isConnected;

      if (focusTarget === 'results') {
        const results = this.querySelector('[data-facets-region="results"]');
        if (results) {
          const top = this.getBoundingClientRect().top + window.scrollY - 16;
          window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
          results.focus({ preventScroll: true });
        }
        return;
      }

      if (!focusIsLost) return;

      const previous = activeId ? document.getElementById(activeId) : null;
      if (previous && !previous.disabled && previous.offsetParent !== null) {
        previous.focus({ preventScroll: true });
        return;
      }

      const drawer = this.querySelector('[data-facets-drawer].is-open');
      if (drawer) {
        const close = drawer.querySelector('.drawer__close');
        if (close) close.focus({ preventScroll: true });
        return;
      }

      if (focusTarget === 'summary') {
        const summary = this.querySelector('[data-facets-region="toolbar-summary"]');
        const fallback = (summary && summary.querySelector('a')) || this.querySelector('[data-facets-sort]') || this.querySelector('[data-facets-region="results"]');
        if (fallback) fallback.focus({ preventScroll: true });
      }
    }

    setLoading(loading) {
      this.classList.toggle('is-loading', loading);
      const results = this.querySelector('[data-facets-region="results"]');
      if (!results) return;
      if (loading) results.setAttribute('aria-busy', 'true');
      else results.removeAttribute('aria-busy');
    }

    syncSort() {
      const select = this.querySelector('[data-facets-sort]');
      if (!select) return;
      const value = new URLSearchParams(window.location.search).get('sort_by') || select.dataset.default;
      if (value && Array.from(select.options).some((option) => option.value === value)) select.value = value;
    }

    /* ---------- Filter UI ---------- */

    setExpanded(facet, expanded) {
      if (!facet) return;
      const button = facet.querySelector('[data-facet-more]');
      facet.classList.toggle('is-expanded', expanded);
      if (!button) return;
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? button.dataset.labelLess : button.dataset.labelMore;
    }

    /* A handle is being dragged: keep the two from crossing and copy the
       prices into the boxes. A handle at either end means no limit there. The
       results reload when the handle is let go (the change event). */
    onRangeInput(range) {
      const facet = range.closest('[data-facet-id]');
      const minRange = facet.querySelector('[data-price-range="min"]');
      const maxRange = facet.querySelector('[data-price-range="max"]');
      const minInput = facet.querySelector('[data-price-input="min"]');
      const maxInput = facet.querySelector('[data-price-input="max"]');
      if (!minRange || !maxRange || !minInput || !maxInput) return;

      if (Number(minRange.value) > Number(maxRange.value)) {
        if (range === minRange) minRange.value = maxRange.value;
        else maxRange.value = minRange.value;
      }
      minInput.value = Number(minRange.value) > 0 ? minRange.value : '';
      maxInput.value = Number(maxRange.value) < Number(maxRange.max) ? maxRange.value : '';
      clearTimeout(this.priceTimer);
      this.updateTrack(facet, false);
    }

    updateTrack(facet, syncRanges = true) {
      if (!facet) return;
      const track = facet.querySelector('[data-price-track]');
      if (!track) return;
      const rangeMax = Number(track.dataset.rangeMax) / 100;
      if (!rangeMax) return;
      const minInput = facet.querySelector('[data-price-input="min"]');
      const maxInput = facet.querySelector('[data-price-input="max"]');
      const clamp = (value) => Math.min(100, Math.max(0, value));
      const min = minInput && minInput.value !== '' ? clamp((Number(minInput.value) / rangeMax) * 100) : 0;
      let max = maxInput && maxInput.value !== '' ? clamp((Number(maxInput.value) / rangeMax) * 100) : 100;
      if (max < min) max = min;
      track.style.setProperty('--from', `${min}%`);
      track.style.setProperty('--to', `${max}%`);
      const minRange = facet.querySelector('[data-price-range="min"]');
      const maxRange = facet.querySelector('[data-price-range="max"]');
      /* With both handles at the far end, the lower one must be on top or neither can move. */
      if (minRange) minRange.style.zIndex = min > 50 ? '2' : '';
      if (!syncRanges) return;
      if (minRange) minRange.value = String((min / 100) * Number(minRange.max));
      if (maxRange) maxRange.value = String((max / 100) * Number(maxRange.max));
    }

    /* ---------- View toggle ---------- */

    readView() {
      try {
        return window.localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
      } catch (e) {
        return 'grid';
      }
    }

    applyView(view) {
      const buttons = this.querySelectorAll('[data-view]');
      const current = buttons.length ? view : 'grid';
      buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.view === current)));
      this.querySelectorAll('[data-facets-grid]').forEach((grid) => grid.classList.toggle('catalog-grid--list', current === 'list'));
    }

    /* ---------- Mobile drawer ---------- */

    openDrawer(trigger) {
      const drawer = this.querySelector('[data-facets-drawer]');
      if (!drawer || desktopQuery.matches) return;
      this.trigger = trigger;
      drawer.classList.add('is-open');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      drawer.setAttribute('aria-labelledby', `${drawer.id}-title`);
      document.body.classList.add('overflow-hidden');
      this.querySelectorAll('[data-facets-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      const close = drawer.querySelector('.drawer__close');
      window.setTimeout(() => {
        if (close) close.focus();
      }, 30);
    }

    closeDrawer(returnFocus) {
      const drawer = this.querySelector('[data-facets-drawer]');
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
      drawer.removeAttribute('aria-labelledby');
      document.body.classList.remove('overflow-hidden');
      this.querySelectorAll('[data-facets-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      if (returnFocus && this.trigger && this.trigger.isConnected) this.trigger.focus();
    }

    trapFocus(event, drawer) {
      const focusable = Array.from(
        drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]')
      ).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);

  /* ------------------------------------------------------------------
     <search-suggestions>: "Did you mean", category hits and "Try instead"
     from /search/suggest.json (queries + collections)
     ------------------------------------------------------------------ */
  class SearchSuggestions extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound || this.hasAttribute('data-disabled')) return;
      this.dataset.bound = 'true';
      const terms = (this.dataset.terms || '').trim();
      const routes = getTheme().routes || {};
      if (!terms || !routes.predictiveSearch) return;
      this.load(terms, routes.predictiveSearch);
    }

    async load(terms, route) {
      const params = new URLSearchParams();
      params.set('q', terms);
      params.set('resources[type]', 'query,collection');
      params.set('resources[limit]', '4');

      try {
        const response = await fetch(`${route}.json?${params.toString()}`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const data = await response.json();
        const results = (data && data.resources && data.resources.results) || {};
        const normalized = terms.toLowerCase();
        const seen = new Set();
        const queries = (results.queries || [])
          .map((query) => String(query.text || '').trim())
          .filter((text) => {
            const key = text.toLowerCase();
            if (!text || key === normalized || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        this.renderQueries(queries.slice(0, 4));
        this.renderCollections((results.collections || []).slice(0, 3));
      } catch (e) {
        /* suggestions are optional */
      }
    }

    searchUrl(text) {
      const params = new URLSearchParams({ q: text, type: 'product', 'options[prefix]': 'last' });
      return `${this.dataset.searchUrl || '/search'}?${params.toString()}`;
    }

    renderQueries(queries) {
      if (!queries.length) return;

      const row = this.querySelector('[data-suggest-queries]');
      const list = row && row.querySelector('[data-suggest-list]');
      if (list) {
        list.replaceChildren(...queries.map((text) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.className = 'chip';
          link.href = this.searchUrl(text);
          link.textContent = text;
          item.appendChild(link);
          return item;
        }));
        row.hidden = false;
      }

      const tryList = this.querySelector('[data-suggest-try]');
      if (tryList) {
        tryList.replaceChildren(...queries.map((text) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = this.searchUrl(text);
          link.textContent = text;
          item.appendChild(link);
          return item;
        }));
        const card = this.querySelector('[data-suggest-try-card]');
        if (card) card.hidden = false;
      }
    }

    renderCollections(collections) {
      const grid = this.querySelector('[data-suggest-collections]');
      if (!grid || !collections.length) return;

      grid.replaceChildren(...collections.map((collection) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'search-category card';
        link.href = collection.url;

        const text = document.createElement('span');
        text.className = 'search-category__text';
        const label = document.createElement('span');
        label.className = 'search-category__label';
        label.textContent = this.dataset.categoryLabel || '';
        const title = document.createElement('span');
        title.className = 'search-category__title';
        title.textContent = collection.title || '';
        text.append(label, title);

        const meta = document.createElement('span');
        meta.className = 'search-category__meta';
        meta.setAttribute('aria-hidden', 'true');
        meta.textContent = `${this.dataset.seeAll || ''} →`;

        link.append(text, meta);
        item.appendChild(link);
        return item;
      }));
      grid.hidden = false;

      const metaTarget = this.querySelector('[data-suggest-category-meta]');
      if (metaTarget) {
        const count = collections.length;
        const strong = document.createElement('strong');
        strong.textContent = String(count);
        const word = count === 1 ? this.dataset.categoryOne : this.dataset.categoryOther;
        metaTarget.replaceChildren(document.createTextNode(' · '), strong, document.createTextNode(` ${word || ''}`));
        metaTarget.hidden = false;
      }
    }
  }

  if (!customElements.get('search-suggestions')) customElements.define('search-suggestions', SearchSuggestions);
})();
